import { Alert, AlertDescription, AlertTitle, Button, cn } from "@ndd/ui";
import { Check, CircleAlert, Loader2, Lock, TriangleAlert } from "lucide-react";
// The explicit React import keeps this module renderable by the node:test
// scenario suites, which compile JSX to the classic runtime.
import React, { type ReactElement, type ReactNode } from "react";
import { EDITOR_FOOTER_FILL } from "./workspace-look";

export type EditorSaveState =
  | "saved"
  | "saving"
  | "failed"
  | "conflict"
  | "not_editable";

const STATUS_LINE =
  "flex items-center gap-1.5 text-xs font-medium transition-opacity duration-150 motion-reduce:transition-none";
const STATUS_ICON = "size-[13px]";

function StatusLine({
  tone,
  children,
}: {
  tone: string;
  children: ReactNode;
}): ReactElement {
  return (
    <span className={cn(STATUS_LINE, tone)} role="status">
      {children}
    </span>
  );
}

/**
 * Autosave status, always visible at the right end of the editor toolbar.
 * Text crossfades at 150ms; the spinner keeps spinning under reduced motion
 * because it is status, not decoration.
 */
export function EditorSaveStatus({
  status,
  savedLabel = "Saved just now",
  locked = false,
  onRetry,
}: {
  status: EditorSaveState;
  savedLabel?: string;
  /** An approved draft: the body is read-only and the status says so. */
  locked?: boolean;
  onRetry: () => void;
}): ReactElement {
  if (locked) {
    return (
      <StatusLine tone="text-muted-foreground">
        <Lock aria-hidden="true" className={STATUS_ICON} />
        Approved
      </StatusLine>
    );
  }

  if (status === "saved") return <SavedStatus savedLabel={savedLabel} />;

  return <UnsavedStatus status={status} onRetry={onRetry} />;
}

function SavedStatus({ savedLabel }: { savedLabel: string }): ReactElement {
  return (
    <StatusLine tone="text-muted-foreground">
      <Check aria-hidden="true" className={cn(STATUS_ICON, "text-success")} />
      <span className="sm:hidden">Saved</span>
      <span className="hidden sm:inline">{savedLabel}</span>
    </StatusLine>
  );
}

function UnsavedStatus({
  status,
  onRetry,
}: {
  status: Exclude<EditorSaveState, "saved">;
  onRetry: () => void;
}): ReactElement {
  switch (status) {
    case "saving":
      return (
        <StatusLine tone="text-muted-foreground">
          <Loader2 aria-hidden="true" className={cn(STATUS_ICON, "animate-spin")} />
          Saving…
        </StatusLine>
      );
    case "failed":
      return (
        <StatusLine tone="text-danger">
          <CircleAlert aria-hidden="true" className={STATUS_ICON} />
          Couldn&apos;t save
          <span aria-hidden="true">·</span>
          <button type="button" className="font-semibold underline" onClick={onRetry}>
            Retry
          </button>
        </StatusLine>
      );
    case "conflict":
      return (
        <StatusLine tone="text-warning">
          <TriangleAlert aria-hidden="true" className={STATUS_ICON} />
          Save paused
        </StatusLine>
      );
    case "not_editable":
      return (
        <StatusLine tone="text-danger">
          <CircleAlert aria-hidden="true" className={STATUS_ICON} />
          Save stopped
        </StatusLine>
      );
  }
}

/**
 * Revision conflict, shown inside the editor above the text. Amber: a risk
 * worth knowing about, not a broken thing. Both choices are reversible
 * (the other version stays in history), so neither asks twice.
 */
export function EditorConflictBanner({
  subject,
  onUseLatest,
  onSaveMine,
}: {
  subject: "note" | "draft";
  onUseLatest: () => void;
  onSaveMine: () => void;
}): ReactElement {
  return (
    <Alert
      variant="warning"
      className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-none border-x-0 border-t-0 px-4 py-3"
    >
      <TriangleAlert aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <AlertTitle>This {subject} changed in another tab.</AlertTitle>
        <AlertDescription className="mt-1">
          Your text is still here and autosave is paused. Choose which version to keep.
        </AlertDescription>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2 pl-7 sm:pl-0">
        <Button type="button" variant="outline" size="sm" onClick={onUseLatest}>
          Use latest
        </Button>
        <Button type="button" size="sm" onClick={onSaveMine}>
          Save my version instead
        </Button>
      </div>
    </Alert>
  );
}

/** The strip pinned under the editor text: status on the left, actions on the right. */
export function EditorFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-2.5 border-t border-border bg-background px-4 pb-3 pt-2.5",
        EDITOR_FOOTER_FILL,
        className
      )}
    >
      {children}
    </div>
  );
}
